import { Component, computed, input, Signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconUtilitiesService } from '@utilities/icon-utilities.service';
import { camelCase } from 'lodash';
import { IconBadgeComponent } from '../icon-badge/icon-badge.component';

type TPlatform = EPlatform | EPlatformFeature | 'noPlatform';

interface IPlatformConfig {
  backgroundColor: string;
  icon: string;
  noMaskSize?: boolean;
}

@Component({
  imports: [IconBadgeComponent, MatTooltipModule, TranslateModule],
  selector: 'app-social-badge',
  templateUrl: './social-badge.component.html',
})
export class SocialBadgeComponent {
  public type = input.required<TPlatform>();

  public size = input<number>(24);
  public color = input<string>('var(--shikenso-purple-base)');
  public backgroundColor = input<string | null>(null);
  public iconSize = input<number>(14);
  public borderWidth = input<number>(0);
  public borderRadius = input<number>(8);
  public isLive = input<boolean>(false);
  public showAuthenticationIcon = input<boolean>(false);
  public showTooltip = input<boolean>(false);
  public iconHasPlatformColor = input<boolean>(false);

  public typeFormatted: Signal<TPlatform | undefined> = computed(() => camelCase(this.type()) as TPlatform);
  public currentConfig: Signal<IPlatformConfig | undefined> = computed(
    () => this.platformConfig[this.typeFormatted() ?? 'noPlatform'],
  );

  public readonly platformConfig: Record<TPlatform, IPlatformConfig> = {
    noPlatform: {
      backgroundColor: 'var(--shikenso-pink-100)',
      icon: IconUtilitiesService.getPlatformFeatureIcon('noPlatform'),
    },
    [EPlatform.twitch]: {
      backgroundColor: 'var(--shikenso-social-twitch)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.twitchStream),
    },
    [EPlatform.instagram]: {
      backgroundColor: 'var(--shikenso-social-instagram)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.instagramPost),
    },
    [EPlatform.youtube]: {
      backgroundColor: 'var(--shikenso-social-youtube)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.youtubeStream),
    },
    [EPlatform.tiktok]: {
      backgroundColor: 'var(--shikenso-social-tiktok)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.tiktokPost),
    },
    [EPlatform.facebook]: {
      backgroundColor: 'var(--shikenso-social-facebook)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.facebookPost),
    },
    [EPlatform.x]: {
      backgroundColor: 'var(--shikenso-social-x)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.xPost),
    },
    [EPlatformFeature.twitchStream]: {
      backgroundColor: 'var(--shikenso-social-twitch)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.twitchStream),
    },
    [EPlatformFeature.instagramPost]: {
      backgroundColor: 'var(--shikenso-social-instagram)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.instagramPost),
    },
    [EPlatformFeature.instagramReel]: {
      backgroundColor: 'var(--shikenso-social-instagram)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.instagramReel),
    },
    [EPlatformFeature.instagramStory]: {
      backgroundColor: 'var(--shikenso-social-instagram)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.instagramStory),
    },
    [EPlatformFeature.tiktokPost]: {
      backgroundColor: 'var(--shikenso-social-tiktok)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.tiktokPost),
    },
    [EPlatformFeature.youtubeStream]: {
      backgroundColor: 'var(--shikenso-social-youtube)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.youtubeStream),
    },
    [EPlatformFeature.youtubeVod]: {
      backgroundColor: 'var(--shikenso-social-youtube)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.youtubeVod),
    },
    [EPlatformFeature.youtubeShort]: {
      backgroundColor: 'var(--shikenso-social-youtube)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.youtubeShort),
      noMaskSize: true,
    },
    [EPlatformFeature.facebookPost]: {
      backgroundColor: 'var(--shikenso-social-facebook)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.facebookPost),
    },
    [EPlatformFeature.facebookVideo]: {
      backgroundColor: 'var(--shikenso-social-facebook)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.facebookVideo),
    },
    [EPlatformFeature.xPost]: {
      backgroundColor: 'var(--shikenso-social-x)',
      icon: IconUtilitiesService.getPlatformFeatureIcon(EPlatformFeature.xPost),
    },
  };

  public readonly computedBackgroundColor = computed(
    () =>
      this.backgroundColor() ?? this.currentConfig()?.backgroundColor ?? this.platformConfig.noPlatform.backgroundColor,
  );

  public readonly computedColor = computed(() => {
    if (this.iconHasPlatformColor()) {
      return this.currentConfig()?.backgroundColor ?? this.platformConfig.noPlatform.backgroundColor;
    }

    return this.color();
  });
}
